import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Link,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type AdminContactDto,
  type DuplicateOrganizationDto,
  MergeRequestDirection,
  OrganizationRoleType,
  useAcceptOrganizationMergeMutation,
  useDeclineOrganizationMergeMutation,
  useListDuplicateOrganizationsQuery,
  useRequestOrganizationMergeMutation,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../../common/errors/useApiError";
import { isApiErrorDto } from "../../../../common/errors/isApiErrorDto";
import { isFetchBaseQueryError } from "../../../../common/errors/isFetchBaseQueryError";
import { displayDate } from "../../../../common/formatting/displayDate";
import { setOrganizationId } from "../../../../common/local-storage/selected-organization";
import { SettingsCard } from "./SettingsCard";

interface Company {
  id: string;
  name: string;
}

interface MergeResult {
  winner: Company;
  ownIsKept: boolean;
}

const REQUEST_NO_LONGER_OPEN = [
  "zz_OrganizationApiErrorCode_MergeRequestExpired",
  "zz_OrganizationApiErrorCode_MergeRequestCancelled",
  "zz_OrganizationApiErrorCode_MergeRequestNotPending",
  "zz_OrganizationApiErrorCode_MergeRequestNotFound",
  "zz_OrganizationApiErrorCode_MergeRequestAlreadyPending",
];

const requestNoLongerOpen = (e: unknown) =>
  isFetchBaseQueryError(e) && isApiErrorDto(e.data) && REQUEST_NO_LONGER_OPEN.includes(e.data.errorCode);

export const DuplicateCompanies = () => {
  const { t } = useTranslation();
  const authState = useLoggedInWithOrgContextState();
  const own: Company = { id: authState.selectedOrg.id, name: authState.selectedOrg.name };
  const { data, isLoading, isError, refetch } = useListDuplicateOrganizationsQuery({ orgId: own.id });
  const [requesting, setRequesting] = useState<DuplicateOrganizationDto | null>(null);
  const [answering, setAnswering] = useState<DuplicateOrganizationDto | null>(null);
  const [mergeResult, setMergeResult] = useState<MergeResult | null>(null);

  const closeStale = () => {
    setRequesting(null);
    setAnswering(null);
    refetch();
  };

  if (mergeResult) {
    return <MergedModal result={mergeResult} onClose={() => setMergeResult(null)} />;
  }
  if (isLoading) {
    return (
      <Flex justify="center" py="16">
        <Spinner color="smPrimary" />
      </Flex>
    );
  }
  if (isError || !data) {
    return (
      <Flex direction="column" align="center" py="16" gap="3">
        <Text color="smError">{t("Could not load the duplicate companies.")}</Text>
        <Button size="sm" variant="outline" onClick={() => refetch()}>
          {t("Try again")}
        </Button>
      </Flex>
    );
  }

  return (
    <>
      <Text fontSize="sm" color="smMuted" mt="5">
        {t(
          "Companies registered separately with the same organization number as yours. Merge them to gather procurements, contracts and users in one company.",
        )}
      </Text>
      {data.duplicates.length === 0 && (
        <Text color="smMuted" mt="5">
          {t("No duplicate companies found")}
        </Text>
      )}
      <List aria-label={t("Duplicate companies") ?? undefined}>
        {data.duplicates.map((duplicate) => (
          <ListItem key={duplicate.id}>
            <DuplicateCard
              duplicate={duplicate}
              own={own}
              onRequest={() => setRequesting(duplicate)}
              onAnswer={() => setAnswering(duplicate)}
            />
          </ListItem>
        ))}
      </List>
      {requesting && (
        <RequestMergeModal
          own={own}
          duplicate={requesting}
          onClose={() => setRequesting(null)}
          onStale={closeStale}
        />
      )}
      {answering && (
        <AnswerMergeModal
          own={own}
          duplicate={answering}
          onClose={() => setAnswering(null)}
          onStale={closeStale}
          onMerged={(result) => {
            setAnswering(null);
            setMergeResult(result);
          }}
        />
      )}
    </>
  );
};

const DuplicateCard = ({
  duplicate,
  own,
  onRequest,
  onAnswer,
}: {
  duplicate: DuplicateOrganizationDto;
  own: Company;
  onRequest: () => void;
  onAnswer: () => void;
}) => {
  const { t } = useTranslation();
  const request = duplicate.pendingRequest;
  const winnerName = request?.winningOrganizationId === own.id ? own.name : duplicate.name;
  return (
    <SettingsCard
      title={duplicate.name}
      subtitle={t("Registered {{date}}", { date: displayDate(duplicate.createdAt) })}
      actions={
        <>
          {request == null && (
            <Button size="sm" colorScheme="teal" onClick={onRequest}>
              {t("Ask to merge")}
            </Button>
          )}
          {request?.direction === MergeRequestDirection.Outgoing && (
            <Badge colorScheme="orange" whiteSpace="normal">
              {t("Requested — awaiting approval")}
            </Badge>
          )}
          {request?.direction === MergeRequestDirection.Incoming && (
            <Button size="sm" colorScheme="teal" onClick={onAnswer}>
              {t("See the merge request")}
            </Button>
          )}
        </>
      }
    >
      {duplicate.details ? (
        <Text fontSize="sm">
          {t("{{members}} users · {{procurements}} procurements · {{contracts}} contracts", {
            members: duplicate.details.memberCount,
            procurements: duplicate.details.procurementCount,
            contracts: duplicate.details.contractCount,
          })}
        </Text>
      ) : (
        <Text fontSize="sm" color="smMuted">
          {t("Its admins and size are shown once {{company}} asks to merge.", { company: duplicate.name })}
        </Text>
      )}
      {request?.direction === MergeRequestDirection.Incoming && (
        <Alert status="info" rounded="md">
          <AlertIcon />
          <AlertDescription fontSize="sm">
            {t("{{company}} asks to merge the two companies. {{winner}} is kept.", {
              company: duplicate.name,
              winner: winnerName,
            })}
          </AlertDescription>
        </Alert>
      )}
      {request?.direction === MergeRequestDirection.Outgoing && (
        <Text fontSize="sm" color="smMuted">
          {t("Expires {{date}} if not answered", { date: displayDate(request.expiresAt) })}
        </Text>
      )}
      {duplicate.details && <AdminList admins={duplicate.details.admins} />}
    </SettingsCard>
  );
};

const AdminList = ({ admins }: { admins: AdminContactDto[] }) => {
  const { t } = useTranslation();
  return (
    <Box>
      <Text fontSize="sm" fontWeight="semibold">
        {t("Admins")}
      </Text>
      <UnorderedList fontSize="sm" styleType="none" ml="0">
        {admins.map((admin) => (
          <ListItem key={admin.email}>
            {[admin.firstName, admin.lastName].filter(Boolean).join(" ") || admin.email} (
            {admin.role === OrganizationRoleType.Owner ? t("Owner") : t("Admin")}) ·{" "}
            <Link href={`mailto:${admin.email}`} color="smPrimary">
              {admin.email}
            </Link>
          </ListItem>
        ))}
      </UnorderedList>
    </Box>
  );
};

const MergeConsequences = ({ winner, loser }: { winner: string; loser: string }) => {
  const { t } = useTranslation();
  return (
    <UnorderedList fontSize="sm" spacing="1">
      <ListItem>
        {t("Procurements, contracts, users and contact persons move from {{loser}} to {{winner}}.", { loser, winner })}
      </ListItem>
      <ListItem>{t("Owners of {{loser}} become admins of {{winner}}.", { loser, winner })}</ListItem>
      <ListItem>{t("{{loser}} is archived and disappears from the list under «Switch organization».", { loser })}</ListItem>
      <ListItem>{t("The merge cannot be reversed without help from support.")}</ListItem>
    </UnorderedList>
  );
};

const RequestMergeModal = ({
  own,
  duplicate,
  onClose,
  onStale,
}: {
  own: Company;
  duplicate: DuplicateOrganizationDto;
  onClose: () => void;
  onStale: () => void;
}) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const [winnerId, setWinnerId] = useState(own.id);
  const [understood, setUnderstood] = useState(false);
  const [requestMerge, { isLoading }] = useRequestOrganizationMergeMutation();
  const winner = winnerId === own.id ? own.name : duplicate.name;
  const loser = winnerId === own.id ? duplicate.name : own.name;

  const send = async () => {
    try {
      await requestMerge({
        orgId: own.id,
        requestOrganizationMergeRequest: { otherOrganizationId: duplicate.id, winningOrganizationId: winnerId },
      }).unwrap();
      onClose();
    } catch (e) {
      trigger(e);
      if (requestNoLongerOpen(e)) onStale();
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="lg" closeOnOverlayClick={!isLoading} closeOnEsc={!isLoading}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("Ask to merge with {{company}}", { company: duplicate.name })}</ModalHeader>
        <ModalCloseButton isDisabled={isLoading} />
        <ModalBody>
          <Stack spacing="4">
            <Box>
              <Text fontWeight="semibold" pb="2">
                {t("Which company is kept?")}
              </Text>
              <RadioGroup value={winnerId} onChange={setWinnerId}>
                <Stack>
                  <Radio value={own.id}>{own.name}</Radio>
                  <Radio value={duplicate.id}>{duplicate.name}</Radio>
                </Stack>
              </RadioGroup>
            </Box>
            <MergeConsequences winner={winner} loser={loser} />
            <Text fontSize="sm" color="smMuted">
              {t("The admins of {{company}} must confirm the merge before it happens.", { company: duplicate.name })}
            </Text>
            <Checkbox isChecked={understood} onChange={(e) => setUnderstood(e.target.checked)}>
              {t("I understand that the merge cannot be reversed without help from support")}
            </Checkbox>
          </Stack>
        </ModalBody>
        <ModalFooter gap="3">
          <Button size="sm" variant="ghost" isDisabled={isLoading} onClick={onClose}>
            {t("Abort")}
          </Button>
          <Button size="sm" colorScheme="teal" isDisabled={!understood} isLoading={isLoading} onClick={send}>
            {t("Confirm and send")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const AnswerMergeModal = ({
  own,
  duplicate,
  onClose,
  onStale,
  onMerged,
}: {
  own: Company;
  duplicate: DuplicateOrganizationDto;
  onClose: () => void;
  onStale: () => void;
  onMerged: (result: MergeResult) => void;
}) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const [understood, setUnderstood] = useState(false);
  const [accept, { isLoading: isAccepting }] = useAcceptOrganizationMergeMutation();
  const [decline, { isLoading: isDeclining }] = useDeclineOrganizationMergeMutation();
  const request = duplicate.pendingRequest;
  if (!request) return null;
  const busy = isAccepting || isDeclining;
  const ownIsKept = request.winningOrganizationId === own.id;
  const winner = ownIsKept ? own : { id: duplicate.id, name: duplicate.name };
  const loser = ownIsKept ? duplicate.name : own.name;

  const answer = async (send: () => Promise<unknown>, done: () => void) => {
    try {
      await send();
      done();
    } catch (e) {
      trigger(e);
      if (requestNoLongerOpen(e)) onStale();
    }
  };

  const confirm = () =>
    answer(
      () => accept({ orgId: own.id, requestId: request.id }).unwrap(),
      () => onMerged({ winner, ownIsKept }),
    );

  const reject = () => answer(() => decline({ orgId: own.id, requestId: request.id }).unwrap(), onClose);

  return (
    <Modal isOpen onClose={onClose} size="lg" closeOnOverlayClick={!busy} closeOnEsc={!busy}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("{{company}} asks to merge", { company: duplicate.name })}</ModalHeader>
        <ModalCloseButton isDisabled={busy} />
        <ModalBody>
          <Stack spacing="4">
            {duplicate.details && <AdminList admins={duplicate.details.admins} />}
            <Text>
              <Text as="span" fontWeight="semibold">
                {t("Company kept")}:
              </Text>{" "}
              {winner.name}
            </Text>
            <MergeConsequences winner={winner.name} loser={loser} />
            <Checkbox isChecked={understood} onChange={(e) => setUnderstood(e.target.checked)}>
              {t("I understand that the merge cannot be reversed without help from support")}
            </Checkbox>
          </Stack>
        </ModalBody>
        <ModalFooter gap="3">
          <Button size="sm" variant="outline" isDisabled={isAccepting} isLoading={isDeclining} onClick={reject}>
            {t("Reject")}
          </Button>
          <Button
            size="sm"
            colorScheme="teal"
            isDisabled={!understood || isDeclining}
            isLoading={isAccepting}
            onClick={confirm}
          >
            {t("Confirm merge")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const MergedModal = ({ result, onClose }: { result: MergeResult; onClose: () => void }) => {
  const { t } = useTranslation();
  const goToWinner = () => {
    setOrganizationId(result.winner.id);
    window.location.replace(import.meta.env.VITE_WEBAPP_BASE_URL);
  };
  const leave = result.ownIsKept ? onClose : goToWinner;
  return (
    <Modal isOpen onClose={leave} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("The companies are merged")}</ModalHeader>
        <ModalBody>
          <Text>
            {t("Procurements, contracts, users and contact persons now belong to {{winner}}.", {
              winner: result.winner.name,
            })}
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" colorScheme="teal" onClick={leave}>
            {result.ownIsKept ? t("Close") : t("Go to {{company}}", { company: result.winner.name })}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
